import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import SplashScreen from "./components/SplashScreen";
import { pingHealth } from "./utils/apiClient";

const WelcomePage = lazy(() => import("./pages/WelcomePage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const IntroPage = lazy(() => import("./pages/IntroPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const PublicKanbanPage = lazy(() => import("./pages/PublicKanbanPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));

// Global Error Boundary (REQ-007)
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#0F172A] text-[#F8FAFC] p-6 text-center">
          <svg
            className="w-16 h-16 text-[#EF4444] mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            ></path>
          </svg>
          <h1 className="text-2xl font-bold mb-2">Something went wrong.</h1>
          <p className="text-[#94A3B8] mb-6">
            The application encountered a critical error. Please reload the
            page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="cta-button bg-[#6366F1] hover:bg-[#4F46E5] text-[#F8FAFC] rounded font-medium transition py-2.5 px-4"
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Pre-emptively ping backend health endpoint asynchronously without blocking render
    setTimeout(() => { pingHealth(); }, 0);

    const timer = window.setTimeout(() => setShowSplash(false), 80);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <ErrorBoundary>
      {showSplash && <SplashScreen />}
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="app-shell min-h-screen w-full font-['Inter']">
            <div className="app-surface w-full min-h-screen relative flex flex-col">
              <Suspense fallback={<div className="min-h-screen bg-[#0F172A] flex items-center justify-center text-indigo-400 font-bold text-xs tracking-wide">VoiceTodo Loading...</div>}>
                <Routes>
                <Route path="/" element={<WelcomePage />} />
                <Route path="/welcome" element={<WelcomePage />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/intro" element={<IntroPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/public/projects/:shareToken" element={<PublicKanbanPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </div>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
